import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkPasswordStrengthFn } from './passwordUtils';
import { handlePasswordRegistration as handleRegAction } from './registrationActions';
import { INITIAL_INTRODUCEE_PROFILE } from './IntroduceeProfileSection';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INITIAL_PASSWORD_STRENGTH = { score: 0, label: '', color: '', checks: { length: false, uppercase: false, lowercase: false, number: false, special: false } };

export function useRegistration(token) {
  const navigate = useNavigate();
  const hasValidated = useRef(false);
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(INITIAL_PASSWORD_STRENGTH);
  const [profileData, setProfileData] = useState(INITIAL_INTRODUCEE_PROFILE);

  const updateProfile = (key, value) => setProfileData(p => ({ ...p, [key]: value }));
  const toggleProfileType = (type) => setProfileData(p => ({
    ...p,
    investment_types: p.investment_types.includes(type)
      ? p.investment_types.filter(t => t !== type)
      : [...p.investment_types, type],
  }));

  useEffect(() => {
    if (!token) { setError('No registration token provided'); setLoading(false); return; }
    if (hasValidated.current) return;
    hasValidated.current = true;
    validateToken();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { setPasswordStrength(password ? checkPasswordStrengthFn(password) : INITIAL_PASSWORD_STRENGTH); }, [password]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/registration/validate-token/${token}`);
      if (!response.ok) {
        let errorMsg = 'This registration link is invalid or has expired.';
        try { const errorData = await response.json(); if (errorData.detail) errorMsg = errorData.detail; } catch (err) { logger.error('Registration error:', err); }
        throw new Error(errorMsg);
      }
      setTokenData(await response.json());
    } catch (err) {
      let errorMessage = 'This registration link is invalid or has expired.';
      if (err.message && !err.message.includes('json') && !err.message.includes('JSON') && !err.message.includes('body stream')) errorMessage = err.message;
      setError(errorMessage);
    } finally { setLoading(false); }
  };

  const handlePasswordRegistration = (e) => handleRegAction(e, { agreedToTerms, password, confirmPassword, passwordStrength, username, usernameAvailable, token, tokenData, navigate, setIsSubmitting, profileData });

  const checkUsernameAvailability = async (value) => {
    try { setCheckingUsername(true); const response = await fetch(`${BACKEND_URL}/api/users/username/check?username=${value}`); const data = await response.json(); setUsernameAvailable(data.available); }
    catch (err) { logger.error('Error checking username:', err); }
    finally { setCheckingUsername(false); }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value.toLowerCase().trim();
    setUsername(value); setUsernameAvailable(null);
    clearTimeout(window.regUsernameTimeout);
    if (value.length >= 3) { setCheckingUsername(true); window.regUsernameTimeout = setTimeout(() => checkUsernameAvailability(value), 500); }
    else { setCheckingUsername(false); }
  };

  return {
    tokenData, loading, error, password, setPassword, confirmPassword, setConfirmPassword,
    username, usernameAvailable, checkingUsername, showPassword, setShowPassword,
    showConfirmPassword, setShowConfirmPassword, agreedToTerms, setAgreedToTerms,
    isSubmitting, passwordStrength, handlePasswordRegistration, handleUsernameChange, navigate,
    profileData, updateProfile, toggleProfileType,
  };
}
